import React, { useMemo } from 'react';

import { Tag } from 'antd';

import styled from 'styled-components';

import { TAG_FILTER_MODE_DEFAULT } from '@libs/NwUtils';

import useTagCatalog from '@hooks/CachedLists/UseTagCatalog';

import NwIcon from '@components/Gui/NwIcon';
import StyleVariables from '@components/Gui/StyleVariables';
import TagPicker from '@components/TagPicker/TagPicker';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

// Il trigger e' un campo, non un gruppo di bottoni: deve leggersi come le select che gli
// stanno accanto nelle colonne di filtri. Misure prese da .ant-select-selector: altezza 28,
// raggio 4, padding orizzontale 11, testo .9rem in TextColor.
const FilterField = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    min-width: 200px;
    min-height: 28px;
    padding: 0 11px;
    border: ${(props) => props.theme.DarkerBorder};
    border-radius: 4px;
    background-color: ${(props) => props.theme.BackgroundColorLight};
    color: ${(props) => props.theme.TextColor};

    &:hover {
        border-color: ${(props) => props.theme.PrimaryColor};
    }

    &.is-set {
        border-color: ${(props) => props.theme.PrimaryColor};
    }

    .field-label {
        flex: 1;
        min-width: 0;
        display: flex;
        align-items: center;
        gap: 0.3rem;
        /* stessa misura del testo dentro .ant-select-selection-item */
        font-size: 0.9rem;

        .ant-tag {
            margin: 0;
            text-transform: lowercase;
        }

        .more {
            font-family: ${(props) => props.theme.FontFamilyMonospace};
            font-size: ${(props) => props.theme.FontSizeXSm};
        }
    }

    /* come la freccia della select: stessa dimensione e stesso appoggio al bordo destro */
    .field-icon,
    .field-tail {
        flex: none;
        font-size: ${(props) => props.theme.FontSizeSm};
        color: ${(props) => props.theme.TextColorMuted};
    }

    .field-tail.clear:hover {
        color: ${(props) => props.theme.TextColor};
    }
`;

/**
 * Filtro per tag di una lista. I valori restano nomi di tag, come prima, ma le opzioni
 * arrivano dal catalogo condiviso invece che dai soli record gia' caricati: con una lista
 * paginata l'elenco non e' piu' parziale.
 *
 * Nessuno stato locale: il valore e' interamente controllato dal consumer, quindi sparisce
 * la risincronizzazione useState/useEffect del vecchio FilterTagSelector.
 *
 * onChange(filterName, values) mantiene la firma usata dai consumer esistenti.
 * filterMode e onFilterModeChange portano la scelta and/or, che appartiene al singolo
 * filtro: il consumer la tiene insieme ai nomi selezionati e la passa a matchesTagFilter.
 */
const TagFilterPicker = ({
    itemType,
    selectedOptions = [],
    onChange,
    filterName = 'tagFilter',
    filterMode = TAG_FILTER_MODE_DEFAULT,
    onFilterModeChange,
    zIndex,
    ...rest
}) => {
    const catalog = useTagCatalog(itemType);

    const options = useMemo(
        () =>
            catalog.map((tag) => ({
                key: tag.Name,
                name: tag.Name,
                usageCount: tag.UsageCount,
                isPrivate: tag.isPrivate,
            })),
        [catalog]
    );

    const selected = useMemo(() => selectedOptions.map((name) => ({ key: name, name })), [selectedOptions]);

    const handleToggle = (option) => {
        const nextValues = selectedOptions.includes(option.key)
            ? selectedOptions.filter((name) => name !== option.key)
            : [...selectedOptions, option.key];
        onChange(filterName, nextValues);
    };

    const handleClearAll = () => {
        onChange(filterName, []);
    };

    const hasFilter = selectedOptions.length > 0;

    // niente tag per questo tipo di oggetto: il filtro non ha nulla da offrire e sparisce,
    // come faceva il vecchio FilterTagSelector quando la lista dei nomi era vuota
    if (!options.length && !hasFilter) {
        return null;
    }

    const triggerLabel = hasFilter ? `Filter by tag: ${selectedOptions.join(` ${filterMode} `)}` : 'Filter by tag';

    const trigger = (
        <FilterField className={hasFilter ? 'is-set' : ''}>
            <NwIcon className="field-icon" icon={byPrefixAndName.fasl['tag']} />
            <span className="field-label">
                {hasFilter ? (
                    <>
                        <Tag color={StyleVariables.PrimaryColor}>{selectedOptions[0]}</Tag>
                        {selectedOptions.length > 1 && <span className="more">+{selectedOptions.length - 1}</span>}
                    </>
                ) : (
                    'Any tag'
                )}
            </span>
            {hasFilter ? (
                <span
                    className="field-tail clear"
                    title="Clear tag filter"
                    onClick={(event) => {
                        // il click non deve arrivare al trigger, che aprirebbe il pannello
                        event.stopPropagation();
                        handleClearAll();
                    }}
                >
                    <NwIcon icon={byPrefixAndName.fasl['times']} />
                </span>
            ) : (
                <span className="field-tail">
                    <NwIcon icon={byPrefixAndName.fasl['chevron-down']} />
                </span>
            )}
        </FilterField>
    );

    return (
        <TagPicker
            title="Filter by tag"
            trigger={trigger}
            triggerLabel={triggerLabel}
            zIndex={zIndex}
            options={options}
            selected={selected}
            onToggle={handleToggle}
            onClearAll={handleClearAll}
            filterMode={filterMode}
            onFilterModeChange={onFilterModeChange}
            selectedLabel="filtering by"
            emptyMessage="No tags defined for this object type"
            {...rest}
        />
    );
};

export default TagFilterPicker;
